import React, { useEffect, useRef, useState } from "react";
import { Camera, Check, CheckCircle, Layers, MapPin, Pencil, Upload, X } from "lucide-react";
import { downloadTextFile, groupByLocation, MissingPhotoRow, toCsv, toTsv } from "../util/photoCoverage";
import { EnrollmentChip } from "./enrollment/EnrollmentChip";

/**
 * Employees Needing Photos — v2 wide modal (Employees page, TAS-220 + TAS-568).
 * Rendered inside the page's `.emp` scope so it reuses the shared emp-modal classes.
 * Rows arrive already scope-filtered (Location / Supervisor / Status, photo filter ignored)
 * and ordered failed-first then by name; this component only groups, renders, copies and
 * exports them. The gap list is no photo + failed enrollment; verifying is excluded (it may pass).
 */
interface EmployeesNeedingPhotosModalProps {
    rows: MissingPhotoRow[];
    /** e.g. "12 employees · Location 101 — Anaheim, CA · Active" */
    scopeLabel: string;
    /** Edit permission: the row action reads Fix / Upload; otherwise View. */
    canEdit: boolean;
    /** Opens the shared Enrollment Photo modal for that employee. */
    onOpen: (employeeID: number) => void;
    onClose: () => void;
    onToast: (kind: "ok" | "err", message: React.ReactNode) => void;
}

const DASH = "—";
const CSV_FILE_NAME = "employees-needing-photos.csv";

function initials(first: string, last: string): string {
    return `${(first || " ")[0]}${(last || " ")[0]}`.toUpperCase();
}

const EmployeesNeedingPhotosModal: React.FC<EmployeesNeedingPhotosModalProps> = ({
    rows,
    scopeLabel,
    canEdit,
    onOpen,
    onClose,
    onToast,
}) => {
    const [copied, setCopied] = useState(false);
    const copiedTimer = useRef<number | null>(null);

    useEffect(() => {
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [onClose]);

    useEffect(() => () => { if (copiedTimer.current) window.clearTimeout(copiedTimer.current); }, []);

    const copyList = async () => {
        try {
            await navigator.clipboard.writeText(toTsv(rows));
            setCopied(true);
            if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
            copiedTimer.current = window.setTimeout(() => setCopied(false), 1800);
            onToast(
                "ok",
                <>
                    Copied <b>{rows.length}</b> {rows.length === 1 ? "employee" : "employees"} to the clipboard
                </>
            );
        } catch {
            onToast("err", "Couldn’t copy the list — download the CSV instead.");
        }
    };

    const exportCsv = () => {
        try {
            downloadTextFile(CSV_FILE_NAME, toCsv(rows), "text/csv");
            onToast(
                "ok",
                <>
                    Exported <b>{rows.length}</b> {rows.length === 1 ? "employee" : "employees"} to <b>{CSV_FILE_NAME}</b>
                </>
            );
        } catch {
            onToast("err", "Couldn’t build the CSV export.");
        }
    };

    const groups = groupByLocation(rows);
    const nFailed = rows.filter((r) => r.state === "failed").length;

    return (
        <div className="emp-modal-bg" onClick={onClose}>
            <div className="emp-modal emp-modal-wide emp-mp-modal" onClick={(e) => e.stopPropagation()}>
                <div className="emp-modal-head">
                    <div className="mi">
                        <Camera size={21} />
                    </div>
                    <div>
                        <h3>Employees Needing Photos</h3>
                        <div className="ms">{scopeLabel}</div>
                    </div>
                    <button type="button" className="x" onClick={onClose} title="Close">
                        <X size={16} />
                    </button>
                </div>

                <div className="emp-modal-body">
                    {rows.length === 0 ? (
                        <div className="emp-mp-empty">
                            <CheckCircle size={22} />
                            <div>
                                <div className="emp-mp-empty-t">Everyone in this view has a working photo</div>
                                <div className="emp-mp-empty-s">
                                    Clear the location or supervisor filters to check the rest of the roster.
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="emp-mp-sum">
                                {rows.length - nFailed > 0 && (
                                    <span>
                                        <b>{rows.length - nFailed}</b> need a photo
                                    </span>
                                )}
                                {nFailed > 0 && (
                                    <span>
                                        <b>{nFailed}</b> failed enrollment: photo on file, needs a fix
                                    </span>
                                )}
                            </div>
                            <div className="emp-mp-head">
                                <span>Employee</span>
                                <span>Reports to</span>
                                <span>Status</span>
                                <span />
                            </div>
                            {groups.map((g) => (
                                <div className="emp-mp-group" key={g.label}>
                                    <div className="emp-mp-glab">
                                        <MapPin size={14} />
                                        {g.label}
                                        <span className="emp-mp-gcount">{g.rows.length}</span>
                                    </div>
                                    {g.rows.map((r) => (
                                        <div className="emp-mp-row" key={r.employeeID}>
                                            <span className={`av sm ${r.state === "failed" ? "enfail" : "nophoto"}`}>
                                                {initials(r.firstName, r.lastName)}
                                            </span>
                                            <div>
                                                <div className="emp-mp-nm">{r.name}</div>
                                                <div className="emp-mp-sub emp-mono">
                                                    {r.altEmployeeID || r.employeeID} · {r.role}
                                                </div>
                                            </div>
                                            <div className="emp-mp-sup">{r.supervisor || DASH}</div>
                                            <div className="emp-mp-stat">
                                                {r.state === "failed" && r.enrollment ? (
                                                    <EnrollmentChip enrollment={r.enrollment} />
                                                ) : (
                                                    <span className="need-tag">Needs a photo</span>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className="emp-btn sm"
                                                onClick={() => onOpen(r.employeeID)}
                                            >
                                                {!canEdit ? (
                                                    "View"
                                                ) : r.state === "failed" ? (
                                                    <>
                                                        <Pencil size={14} /> Fix
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload size={14} /> Upload
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </>
                    )}
                </div>

                <div className="emp-modal-foot emp-mp-foot">
                    <button type="button" className="emp-btn" onClick={onClose}>
                        Close
                    </button>
                    {rows.length > 0 && (
                        <>
                            <button type="button" className="emp-btn" onClick={copyList}>
                                {copied ? <Check size={15} /> : <Layers size={15} />}
                                {copied ? "Copied" : "Copy list"}
                            </button>
                            <button type="button" className="emp-btn primary" onClick={exportCsv}>
                                <Upload size={15} /> Download CSV
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmployeesNeedingPhotosModal;
